import { ComponentType, Component } from "@/types";
import { BaseCommandHandler, CommandResult } from "./BaseCommandHandler";

export interface CommandHandlerRegistration {
  componentType: ComponentType | ComponentType[];
  handler: BaseCommandHandler;
  priority?: number; // Higher priority handlers are checked first
}

/**
 * Central registry for all component command handlers
 * Provides dynamic registration and routing of AI commands
 */
interface HandlerWithPriority {
  handler: BaseCommandHandler;
  priority: number;
}

export class CommandRegistry {
  private static instance: CommandRegistry;
  private handlers: Map<ComponentType, HandlerWithPriority[]> = new Map();
  private fallbackHandler?: BaseCommandHandler;

  private constructor() {}

  static getInstance(): CommandRegistry {
    if (!CommandRegistry.instance) {
      CommandRegistry.instance = new CommandRegistry();
    }
    return CommandRegistry.instance;
  }

  /**
   * Register a command handler for one or more component types
   */
  registerHandler(
    componentTypes: ComponentType | ComponentType[],
    handler: BaseCommandHandler,
    priority: number = 0
  ): void {
    const types = Array.isArray(componentTypes) ? componentTypes : [componentTypes];

    types.forEach(type => {
      const existing = this.handlers.get(type) ?? [];
      this.handlers.set(type, existing);
      const handlers = existing;
      handlers.push({ handler, priority });

      // Sort by priority (higher first)
      handlers.sort((a, b) => b.priority - a.priority);
    });
  }

  /**
   * Register a fallback handler for unhandled commands
   */
  registerFallbackHandler(handler: BaseCommandHandler): void {
    this.fallbackHandler = handler;
  }

  /**
   * Get handlers for a specific component type
   */
  getHandlers(componentType: ComponentType): BaseCommandHandler[] {
    const handlerWithPriorities = this.handlers.get(componentType) || [];
    const handlers = handlerWithPriorities.map(h => h.handler);

    // Add fallback handler if available
    if (this.fallbackHandler) {
      handlers.push(this.fallbackHandler);
    }

    return handlers;
  }

  /**
   * Process a command for a component
   */
  async processCommand(
    command: string,
    component: Component
  ): Promise<CommandResult> {
    const handlers = this.getHandlers(component.type);

    if (handlers.length === 0) {
      return {
        success: false,
        error: `No command handlers registered for component type: ${component.type}`,
        suggestions: this.getGenericSuggestions(component.type)
      };
    }

    // Try each handler in priority order
    for (const handler of handlers) {
      if (handler.canHandle(command, component)) {
        const result = await handler.parseCommand(command, component);

        // If handler succeeded or provided specific error, return it
        if (result.success || result.error !== "Command not recognized") {
          return result;
        }
      }
    }

    // No handler could process the command
    return {
      success: false,
      error: "Command not recognized",
      suggestions: await this.getSuggestionsFromHandlers(component, handlers)
    };
  }

  /**
   * Get example commands for a component type
   */
  async getExampleCommands(componentType: ComponentType): Promise<string[]> {
    const handlers = this.getHandlers(componentType);
    const examples: string[] = [];

    for (const handler of handlers) {
      const handlerExamples = await handler.getExampleCommands(componentType);
      examples.push(...handlerExamples);
    }

    // Remove duplicates
    return [...new Set(examples)];
  }

  /**
   * Get suggestions from all handlers for a component
   */
  private async getSuggestionsFromHandlers(
    component: Component,
    handlers: BaseCommandHandler[]
  ): Promise<string[]> {
    const suggestions: string[] = [];

    for (const handler of handlers) {
      const handlerSuggestions = await handler.getSuggestions(component);
      suggestions.push(...handlerSuggestions);
    }

    // Remove duplicates and limit
    return [...new Set(suggestions)].slice(0, 10);
  }

  /**
   * Get generic suggestions for a component type
   */
  private getGenericSuggestions(componentType: ComponentType): string[] {
    const suggestions = [
      "Try a more specific command",
      "Use quotes for exact values",
      "Check the component type"
    ];

    // Add type-specific hints
    switch (componentType) {
      case ComponentType.TEXT:
        suggestions.push(
          "Try: 'make text bold'",
          "Try: 'set font size to 20'",
          "Try: 'center align text'"
        );
        break;
      case ComponentType.BUTTON:
        suggestions.push(
          "Try: 'change label to Submit'",
          "Try: 'make button primary'",
          "Try: 'add click action'"
        );
        break;
      case ComponentType.IMAGE:
        suggestions.push(
          "Try: 'set width to 300'",
          "Try: 'make rounded'",
          "Try: 'add shadow'"
        );
        break;
      case ComponentType.RADIAL_DIAL:
        suggestions.push(
          "Try: 'set range 0 to 255'",
          "Try: 'theme ambient'",
          "Try: 'set unit to lux'"
        );
        break;
      default:
        suggestions.push("Try: 'set width to 200'", "Try: 'change color to blue'");
    }

    return suggestions;
  }

  /**
   * Check if any handler can process a command
   */
  canProcessCommand(command: string, component: Component): boolean {
    const handlers = this.getHandlers(component.type);
    return handlers.some(handler => handler.canHandle(command, component));
  }

  /**
   * Clear all registered handlers (useful for testing)
   */
  clearHandlers(): void {
    this.handlers.clear();
    this.fallbackHandler = undefined;
  }

  /**
   * Get all registered component types
   */
  getRegisteredTypes(): ComponentType[] {
    return Array.from(this.handlers.keys());
  }
}

export const commandRegistry = CommandRegistry.getInstance();
